/**
 * @file BillPoCostDialog.js
 * @version 1.5.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-08
 * @modified 2026-10-13
 *
 * "Update PO from bill" (Cam, 8 Oct 2026): a bill no longer sets its POs'
 * costs by itself. When it would change any PO line's cost, this asks, line
 * by line — PO, product, PO cost → bill cost, and the PO and bill quantities
 * for information (a quantity is never changed by a bill). Each line has a
 * tick; "Update PO from bill" applies the ticked lines, "Keep PO as it is"
 * none. All ticked unless the bill is Queried — then none.
 *
 * Presentational: the caller decides what the answer does (Add / Edit Bill
 * sends it with the save as `po_cost_updates`; UpdatePoFromBillDialog posts
 * it to an existing bill). Closing the window (×) answers nothing.
 *
 * 1.1.0 (2026-10-08, review): a line whose PO already carries the bill's cost
 * but whose order line's cost price differs (or is not set) is listed too,
 * saying so; every line shows the order line's cost old → new where known.
 *
 * 1.3.0 (2026-10-08, Cam): bills only again — a supplier credit never
 * changes a PO's cost, so the credit wording (`labels`, added in 1.2.0) is
 * gone.
 * 1.4.0 (2026-10-12, Bulk Add Bills): the table (BillPoCostBody) is exported,
 * with `idPrefix`, so Accept asks the same question for each bill it saves.
 * 1.5.0 (2026-10-13, Jade): a line billed in another unit (per tonne against
 * a PO in bags) shows its bill cost and quantity in the PO line's unit, with
 * how it was read under the product ("Billed per tonne, PO in packs: …").
 */

import React, { useState } from 'react';
import Modal from './Modal';
import TickCheckbox from './TickCheckbox';
import { useSlideOutButton } from './SlideOutButton';
import { formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import { costKey } from '../utils/billCostWriteBack';
import './BillPoCostDialog.css';

const keyOf = (c) => c.key || costKey(c.purchase_order_id, c.po_line_key);

function BillPoCostBody({ changes, ticks, setTick, queried, busy, error, intro, idPrefix = 'bill-po-cost' }) {
  useSlideOutButton([busy, Object.values(ticks).filter(Boolean).length]);
  const all = changes.length > 0 && changes.every(c => ticks[keyOf(c)]);
  return (
    <div className="bill-po-cost-body">
      <p className="bill-po-cost-intro">
        {intro || 'This bill\'s costs differ from its PO. Tick the lines whose PO cost should become the bill\'s — or keep the PO as it is (a query with the supplier, product and haulage billed separately).'}
      </p>
      {queried && (
        <p className="bill-po-cost-hint">
          <span className="material-symbols-rounded">help</span>
          The bill is Queried, so nothing is ticked: the PO keeps its cost until the query is settled.
        </p>
      )}
      <div className="table-container">
        <table className="data-table bill-po-cost-table">
          <thead>
            <tr>
              <th className="tick-col">
                <TickCheckbox
                  id={`${idPrefix}-all`}
                  checked={all}
                  disabled={busy}
                  ariaLabel="Tick every line"
                  onChange={(e) => changes.forEach(c => setTick(keyOf(c), e.target.checked))}
                />
              </th>
              <th>PO</th>
              <th>Product</th>
              <th className="num">PO cost</th>
              <th className="num">Bill cost</th>
              <th className="num">PO qty</th>
              <th className="num">Bill qty</th>
            </tr>
          </thead>
          <tbody>
            {changes.map(c => {
              const k = keyOf(c);
              const qtyDiffers = c.po_quantity != null && c.bill_quantity != null
                && Math.abs(Number(c.po_quantity) - Number(c.bill_quantity)) > 0.0001;
              return (
                <tr key={k} className={ticks[k] ? 'chosen' : ''}>
                  <td className="tick-col">
                    <TickCheckbox
                      id={`${idPrefix}-${k}`}
                      checked={!!ticks[k]}
                      disabled={busy}
                      ariaLabel={`Update ${c.purchase_order_number || 'the PO'} ${c.description || ''} from the bill`}
                      onChange={(e) => setTick(k, e.target.checked)}
                    />
                  </td>
                  <td>
                    {c.purchase_order_number || `PO #${c.purchase_order_id}`}
                    {c.xero_made && <span className="bill-po-cost-tag" title="Made in Xero: a new cost is sent to Xero's PO too">Xero</span>}
                  </td>
                  <td>
                    {c.description || c.item_code || '—'}
                    {(c.unit_conversion?.cost_text || c.unit_conversion?.qty_text) && (
                      <div className="bill-po-cost-sub">{c.unit_conversion.cost_text || `Billed in another unit: ${c.unit_conversion.qty_text}`}</div>
                    )}
                    {c.order_line_moves && (
                      <div className="bill-po-cost-sub">
                        {c.order_line_only ? 'PO already agrees — ' : ''}order line cost {c.order_line_cost == null ? 'not set' : formatPrice(c.order_line_cost)} → {formatPrice(c.order_line_cost_new)}
                      </div>
                    )}
                  </td>
                  <td className="num">{formatPrice(c.po_cost)}</td>
                  <td className="num"><strong>{formatPrice(c.bill_cost)}</strong></td>
                  <td className={`num ${qtyDiffers ? 'qty-differs' : ''}`}>{c.po_quantity != null ? formatQuantity(c.po_quantity) : '—'}</td>
                  <td className={`num ${qtyDiffers ? 'qty-differs' : ''}`}>{c.bill_quantity != null ? formatQuantity(c.bill_quantity) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="bill-po-cost-note">Quantities are shown for information only — a bill never changes a PO's quantity.</p>
      {error && (
        <div className="bill-po-cost-error" role="alert">
          <span className="material-symbols-rounded">error</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array<object>} props.changes — utils/billCostWriteBack.poCostChanges, or the server's cost_differences
 * @param {Object<string, boolean>} props.initialTicks — utils/billCostWriteBack.defaultCostTicks
 * @param {boolean} [props.queried]
 * @param {boolean} [props.busy]
 * @param {string|null} [props.error]
 * @param {string} [props.title]
 * @param {string} [props.intro]
 * @param {(ticks: Object<string, boolean>) => void} props.onApply — "Update PO from bill"
 * @param {() => void} props.onKeep — "Keep PO as it is"
 * @param {() => void} props.onClose — ×: no answer
 */
function BillPoCostDialog({ changes, initialTicks, queried = false, busy = false, error = null, title, intro, onApply, onKeep, onClose }) {
  const [ticks, setTicks] = useState(() => ({ ...(initialTicks || {}) }));
  const setTick = (k, on) => setTicks(prev => ({ ...prev, [k]: on }));
  const anyTicked = changes.some(c => ticks[keyOf(c)]);
  return (
    <Modal
      isOpen
      onClose={busy ? () => {} : onClose}
      title={title || 'Update PO from bill?'}
      size="lg"
      className="bill-po-cost"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onKeep} disabled={busy} title="Leave every PO line's cost as it is">
              <div className="icon-wrapper"><span className="material-symbols-rounded">block</span></div>
              <span className="btn-label">Keep PO as it is</span>
            </button>
          </div>
          <div>
            <button
              className="btn-slideout btn-slideout-primary"
              onClick={() => onApply(ticks)}
              disabled={busy || !anyTicked}
              title={anyTicked ? 'The ticked PO lines take the bill\'s cost' : 'Tick a line first, or keep the PO as it is'}
            >
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${busy ? ' spinning' : ''}`}>{busy ? 'progress_activity' : 'price_check'}</span>
              </div>
              <span className="btn-label">{busy ? 'Saving…' : 'Update PO from bill'}</span>
            </button>
          </div>
        </div>
      }
    >
      <BillPoCostBody changes={changes} ticks={ticks} setTick={setTick} queried={queried} busy={busy} error={error} intro={intro} />
    </Modal>
  );
}

// The table alone, for Bulk Add Bills' Accept (one per bill, its own ids).
export { BillPoCostBody };
export default BillPoCostDialog;
